import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { useState, useContext } from 'react';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import FormHelperText from '@mui/material/FormHelperText';
import { useNavigate } from 'react-router';

import { ClinicRxContext } from '../../App';
import { getServerURL } from '../../utils';
import { addCalculatedColumns } from '../custom';

const SERVER_URL = getServerURL();

export default function Login({ checkActivity }) {
  const { dispatch } = useContext(ClinicRxContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [usernameErrorMessage, setUsernameErrorMessage] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');

  const handleClickShowPassword = () =>
    setShowPassword((showPassword) => !showPassword);

  const handleMouseDownPassword = (e) => e.preventDefault();
  const handleMouseUpPassword = (e) => e.preventDefault();

  const handleLoginClick = async (e) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${SERVER_URL}/login`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: username,
          password,
        }),
      });
      if (!response.ok) {
        const message = response.text();
        switch (response.status) {
          case 432: {
            setUsernameError(true);
            setUsernameErrorMessage(message);
            return;
          }
          case 433: {
            setPasswordError(true);
            setPasswordErrorMessage(message);
            return;
          }
        }
      } else {
        const { user, alertData, token } = await response.json();
        const prettyAlerts = alertData.map((alert) => {
          return addCalculatedColumns(alert, user.organization);
        });
        dispatch({
          type: 'login',
          user: {
            ...user,
            isAuthenticated: true,
          },
          alertData: prettyAlerts,
          token,
        });
        checkActivity();
        if (user.userType > 1) {
          navigate('/dashboard');
          return;
        }
        navigate('/alert');
      }
    } catch (err) {
      console.log(`Error during login FETCH request: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '20px',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'primary.light',
          width: '450px',
          padding: '100px',
          margin: '75px 0',
          borderRadius: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          border: '7px solid rgba(255, 255, 255, 0.1)',
          boxShadow:
            '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset;',
        }}
      >
        <Typography
          sx={{ color: theme.palette.grey['50'], fontWeight: '20px' }}
        >
          Log in to your ClinicRx account!
        </Typography>
        <Box
          component='form'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
          }}
        >
          <TextField
            error={usernameError}
            helperText={usernameError ? usernameErrorMessage : ''}
            id='username'
            label='Username'
            variant='outlined'
            onChange={(e) => setUsername(e.target.value)}
          />
          <FormControl
            variant='outlined'
            error={passwordError}
          >
            <InputLabel htmlFor='password'>Password</InputLabel>
            <OutlinedInput
              id='password'
              type={showPassword ? 'text' : 'password'}
              onChange={(e) => setPassword(e.target.value)}
              endAdornment={
                <InputAdornment position='end'>
                  <IconButton
                    aria-label={
                      showPassword
                        ? 'hide the password'
                        : 'display the password'
                    }
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    onMouseUp={handleMouseUpPassword}
                    edge='end'
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label='Password'
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  handleLoginClick();
                }
              }}
            />
            <FormHelperText error='true'>
              {passwordError ? passwordErrorMessage : ''}
            </FormHelperText>
          </FormControl>
          <Button
            loading={isLoading}
            variant='contained'
            sx={{
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 20px rgba(76, 94, 255, 0.5)',
              },
            }}
            onClick={handleLoginClick}
          >
            Login
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
